import { useEffect, useRef } from "react";

function isAffectedIosChrome(): boolean {
	if (typeof navigator === "undefined") {
		return false;
	}

	const { userAgent, maxTouchPoints } = navigator;
	const isIosDevice =
		/(iPad|iPhone|iPod)/.test(userAgent) ||
		(userAgent.includes("Macintosh") && maxTouchPoints > 1);

	return isIosDevice && userAgent.includes("CriOS");
}

function clearScrollLock(scrollContainer: HTMLElement | null): void {
	const { documentElement, body } = document;

	documentElement.style.overflow = "";
	documentElement.style.height = "";
	body.style.overflow = "";
	body.style.height = "";

	if (!scrollContainer) {
		return;
	}

	scrollContainer.style.overflowY = "";
	scrollContainer.style.height = "";
	scrollContainer.style.removeProperty("-webkit-overflow-scrolling");
}

/**
 * Locks the browser navigation bar on iOS Chrome by preventing the document
 * from being the scroll container. When `shouldLock` is `true`, this hook
 * sets `overflow: hidden; height: 100%` on `<html>` and `<body>`, then
 * delegates scrolling to the element marked with `[data-storefront-scroll]`.
 *
 * This works around a confirmed iOS 26 WebKit regression (Bugzilla #297779)
 * where `position: fixed; bottom: 0` elements shift ~20-24px when the
 * toolbar hides/shows. Preventing the toolbar from ever collapsing eliminates
 * the trigger entirely.
 *
 * The lock is released when `shouldLock` becomes `false` so that touch
 * interactions on the mobile header (e.g. the delivery-method panel) keep
 * working after the delivery address modal is dismissed. Safari is unaffected
 * — this hook is a no-op outside iOS Chrome (`CriOS`).
 */
export function useIosNavigationLock(shouldLock: boolean): void {
	const scrollContainerRef = useRef<HTMLElement | null>(null);
	const scrollY = useRef(0);

	useEffect(() => {
		if (!isAffectedIosChrome()) {
			return;
		}

		if (!shouldLock) {
			clearScrollLock(scrollContainerRef.current);
			scrollContainerRef.current = null;
			return;
		}

		const scrollContainer = document.querySelector<HTMLElement>(
			"[data-storefront-scroll]",
		);
		if (!scrollContainer) {
			return;
		}

		scrollContainerRef.current = scrollContainer;
		scrollY.current = window.scrollY;

		const { documentElement, body } = document;

		documentElement.style.overflow = "hidden";
		documentElement.style.height = "100%";
		body.style.overflow = "hidden";
		body.style.height = "100%";

		scrollContainer.style.overflowY = "auto";
		scrollContainer.style.height = "100%";
		scrollContainer.style.setProperty("-webkit-overflow-scrolling", "touch");

		scrollContainer.scrollTop = scrollY.current;

		return () => {
			clearScrollLock(scrollContainer);
			scrollContainerRef.current = null;
		};
	}, [shouldLock]);
}
